import { useState } from "react";

import { closestCenter, DndContext, DragOverlay } from "@dnd-kit/core";
import { restrictToWindowEdges } from "@dnd-kit/modifiers";
import { rectSwappingStrategy, SortableContext } from "@dnd-kit/sortable";
import { Anchor, Button } from "@newsela/angelou";

import {
  actionNames,
  actionPrefixes,
  objectTypes,
  withTrackEvent,
} from "@newsela/snitch";

import PropTypes from "prop-types";

import Item from "@/assignments/AssignmentEditor/SortableDeckOfCards/Item";
import SortableItem from "@/assignments/AssignmentEditor/SortableDeckOfCards/SortableItem";
import DeckOfCards from "@/components/DeckOfCards";
import ReorderIcon from "@/icons/ReorderIcon";
import TrashIcon from "@/icons/TrashIcon";
import { BookmarkContextProvider } from "@/users/useBookmarkContent/useBookmarkContent";
import getHref from "@/utils/getHref";
import {
  buttonCss,
  footerCss,
  footerRightCss,
  footerViewLinkCss,
  reorderButtonCss,
} from "./SortableDeckOfCards.style";

const propTypes = {
  className: PropTypes.string,
  // TODO someone who knows for sure what these are should update these types
  deckOfCardsProps: PropTypes.object,
  content_id: PropTypes.number,
  content_type: PropTypes.string,
  user_level_article: PropTypes.shape({ title: PropTypes.string }),
  listeners: PropTypes.object,
  attributes: PropTypes.object,
  /**
   * Object that gets passed down to the Angelou Button. See the Button README for more information.
   */
  deleteCardButtonProps: PropTypes.object,
  /**
   * Function to fire when card is deleted.
   */
  handleDeleteCard: PropTypes.func,
  /**
   * Array of items to sort.
   */
  items: PropTypes.arrayOf(PropTypes.object),
  /**
   * Pass an object with keys and functions related to the sortable actions being taken for the screen reader to announce.
   */
  screenReaderInstructions: PropTypes.shape({
    /**
     * Message to convey when sort is cancelled either by putting activated card back into its initial position
     * of when the esc key is pressed.
     */
    onDragCancel: PropTypes.func,
    /**
     * Message to convey when activated card is dropped into place.
     */
    onDragEnd: PropTypes.func,
    /**
     * Message to convey when activated card is 'in flight'.
     */
    onDragOver: PropTypes.func,
    /**
     * Message to convey when card is activated and able to be sorted.
     */
    onDragStart: PropTypes.func,
  }),
  /*
   * title of CardFooter
   */
  title: PropTypes.string,
  /**
   * Function to fire if sort is cancelled either by putting activated card back into its initial position
   * of when the esc key is pressed.
   */
  onDragCancel: PropTypes.func,
  /**
   * Function to fire when activated card is dropped into place.
   */
  onDragEnd: PropTypes.func,
  /**
   * Function to fire when card is 'in flight'.
   */
  onDragMove: PropTypes.func,
  /**
   * Function to fire when activated card overlaps another sortable item.
   */
  onDragOver: PropTypes.func,
  /**
   * Function to fire when a card is activated and able to be sorted.
   */
  onDragStart: PropTypes.func,
};

const ButtonWithTrackEvent = withTrackEvent(Button);

const SortableDeckOfCards = (props) => {
  const {
    items,
    className,
    deleteCardButtonProps,
    handleDeleteCard,
    onDragStart,
    onDragMove,
    onDragOver,
    onDragEnd,
    onDragCancel,
  } = props;

  // Is a card being actively dragged?
  const [isDragging, setIsDragging] = useState(false);
  // Sets the id of the card being dragged
  const [dragId, setDragId] = useState(null);

  const activeDragCard = items?.findIndex(
    (assignment) => assignment?.content_id === dragId,
  );

  const handleDragStart = (event) => {
    setIsDragging(true);
    setDragId(event.active.id);

    if (onDragStart) {
      onDragStart(event);
    }
  };

  const handleDragEnd = (event) => {
    setIsDragging(false);
    setDragId(null);

    if (onDragEnd) {
      onDragEnd(event);
    }
  };

  const CardFooter = (props) => {
    return (
      <div css={footerCss}>
        <Anchor
          ariaProps={{
            "aria-label": `View ${
              props?.title || props?.user_level_article?.title
            }. Link opens in a new tab`,
          }}
          href={`${getHref(props)}/?preview_for=assignment`}
          __cssFor={{
            root: footerViewLinkCss,
          }}
          openInNewTab
        >
          {props?.content_type === "externallink" ? "Open Resource" : "View"}
        </Anchor>
        {items.length > 1 ? (
          <div css={footerRightCss}>
            <ButtonWithTrackEvent
              __cssFor={{
                root: [buttonCss, reorderButtonCss],
              }}
              flavor={Button.flavors.incognito}
              ariaProps={{
                "aria-label": `Reorder ${
                  props?.title || props?.user_level_article?.title
                }.`,
                // using ariaProps here as a way to spread props on the DOM node.
                ...props?.listeners,
                ...props?.attributes,
              }}
              objectType={objectTypes.assignment}
              actionPrefix={actionPrefixes.initiate}
              actionName={actionNames.update}
              trackView={false}
              trackImpression={false}
              streamProperties={{
                content_id: props?.content_id,
                content_type: props?.content_type,
                description: "Content reordered",
              }}
            >
              <ReorderIcon />
            </ButtonWithTrackEvent>
            <ButtonWithTrackEvent
              ariaProps={{
                "aria-label": `Remove ${
                  props?.title || props?.user_level_article?.title
                } from assignment`,
              }}
              __cssFor={{
                root: buttonCss,
              }}
              flavor={Button.flavors.incognito}
              onClick={(e) => handleDeleteCard(e, props?.content_id)}
              objectType={objectTypes.assignment}
              actionPrefix={actionPrefixes.initiate}
              actionName={actionNames.update}
              trackView={false}
              trackImpression={false}
              streamProperties={{
                content_id: props?.content_id,
                content_type: props?.content_type,
                description: "Content removed",
              }}
              {...deleteCardButtonProps}
            >
              <TrashIcon />
            </ButtonWithTrackEvent>
          </div>
        ) : null}
      </div>
    );
  };

  const dragDeck = items?.map((assignment) => {
    return (
      <Item key={assignment?.content_id} id={assignment?.content_id}>
        <DeckOfCards
          key={assignment?.content_id}
          id={assignment?.content_id}
          footer={<CardFooter {...assignment} />}
          {...assignment}
          className={className}
        />
      </Item>
    );
  });

  /**
   * The default screen reader announcements were only reading out content_id numbers which isn't useful for identifying
   * content aurally. Creating the newAnnouncement object was necessary for reading out the content title.
   */
  const getCardById = (id) =>
    items.find((assignment) => assignment?.content_id === id);

  const newAnnouncements = {
    onDragStart(id) {
      const { title, user_level_article: legacyTitle } = getCardById(id);
      return `Picked up draggable content ${title || legacyTitle?.title}.`;
    },
    onDragOver(id, overId) {
      const { title, user_level_article: legacyTitle } = getCardById(id);
      const { title: overTitle, user_level_article: overTitleLegacyTitle } =
        getCardById(overId);
      if (overId) {
        return `Draggable content ${
          title || legacyTitle?.title
        } was moved over droppable area currently occupied by ${
          overTitle || overTitleLegacyTitle?.title
        } content.`;
      }

      return `Draggable content ${
        title || legacyTitle?.title
      } is no longer over a droppable area.`;
    },
    onDragEnd(id, overId) {
      const { title, user_level_article: legacyTitle } = getCardById(id);
      const { title: overTitle, user_level_article: overTitleLegacyTitle } =
        getCardById(overId);
      if (overId) {
        return `Draggable content was dropped over droppable area previously occupied by ${
          overTitle || overTitleLegacyTitle?.title
        }`;
      }

      return `Draggable content ${title || legacyTitle?.title} was dropped.`;
    },
    onDragCancel(id) {
      const { title, user_level_article: legacyTitle } = getCardById(id);
      return `Dragging was cancelled. Draggable content ${
        title || legacyTitle?.title
      } was dropped.`;
    },
  };

  return (
    <DndContext
      announcements={newAnnouncements}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragMove={onDragMove}
      onDragOver={onDragOver}
      onDragEnd={handleDragEnd}
      onDragCancel={onDragCancel}
    >
      <BookmarkContextProvider>
        <SortableContext
          items={items?.map((assignment) => assignment?.content_id)}
          id={items?.map((assignment) => assignment?.content_id)}
          strategy={rectSwappingStrategy}
        >
          {items?.map((assignment) => {
            return (
              <SortableItem
                id={assignment?.content_id}
                key={assignment?.content_id}
                deckOfCardsProps={{
                  className,
                  footer: (sortableProps) => (
                    <CardFooter {...assignment} {...sortableProps} />
                  ),
                  href: "",
                  ...assignment,
                  ...props.deckOfCardsProps,
                }}
              />
            );
          })}
          <DragOverlay modifiers={[restrictToWindowEdges]}>
            {isDragging ? dragDeck[activeDragCard] : null}
          </DragOverlay>
        </SortableContext>
      </BookmarkContextProvider>
    </DndContext>
  );
};

SortableDeckOfCards.propTypes = propTypes;
SortableDeckOfCards.Item = Item;
export default SortableDeckOfCards;
